import Parse from 'parse';

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of, throwError } from 'rxjs';
import { concatMap, delay, retryWhen } from 'rxjs/operators';
import { apiUrl } from '@telespot/shared/environment';

import {
  FindingVersionRef,
  GetSampleCounterRequest,
  MosaicItem,
  MosaicMapper,
  Roi,
  RoiFinding,
} from '@analysis/domain';
import {
  ListForAssetInSamplePayload,
  type RoiRepository,
  ListRoisResponse,
  ListForAnalystInSamplePayload,
  type RoiCropRequest,
  UpdateRoisPayload,
  type ProjectionWatermarksPayload,
  type ProjectionWatermarksResponse,
} from '@analysis/use-cases';
import { ListSampleRoisPayload } from '@analysis/use-cases';
import { AbortableHttp } from '@shared/infra/http';
import {
  AlgorithmTopology,
  AnalysisTopology,
  FindingTopology,
  LatestVersionTopology,
  ObjectTopology,
  ParseBaseRepository,
  SampleTopology,
  UserTopology,
  AssetTopology,
} from '@telespot/infrastructure/parse';
import { StepTask } from '@telespot/domain';
import { TelespotRoiMapper } from './telespot-roi.mapper';
import { TelespotRoiCropRepository } from './telespot-roi-crop.repository';
import { ApiAuthErrors } from '@telespot/web-core';
import { cached, invalidatesCache } from '@shared/decorators';

export enum TelespotRoiErrors {
  LOAD_LABELS_ERROR = 'rois.load_labels_error',
  LOAD_BLOB_ERROR = 'rois.load_blob_error',
}

interface MosaicResponse {
  items: MosaicItem[];
  nextToken?: string;
}

/** Body of `GET /samples/{id}/projections`. */
interface ProjectionResponse {
  complete: boolean;
  watermarks: { findingId: string; projectedVersion: number; projectedVersionId: string }[];
}

@Injectable({
  providedIn: 'root', // TODO: remove this when global roi store is removed
})
export class TelespotRoiRepository extends ParseBaseRepository implements RoiRepository {
  private readonly BATCH_SIZE = 50;
  private readonly CONCURRENT_GETS = 20;
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly RETRY_DELAY_MS = 200;
  private readonly FINDING_TYPES = [StepTask.POSITION, StepTask.ROIDETECTION];
  private readonly FINDING_FIELDS_TO_INCLUDE = [FindingTopology.ANALYSIS, FindingTopology.PIPELINE_STEP];

  private readonly mapper = new TelespotRoiMapper(this.parse);

  constructor(
    private readonly http: HttpClient,
    private readonly abortableHttp: AbortableHttp,
    private readonly crops: TelespotRoiCropRepository
  ) {
    super(Parse, { useMasterKey: false });
  }

  /**
   * Saves the analyst's changes to Parse, and only to Parse.
   *
   * Parse is the source of record, and the projection mirrors it into the mosaic
   * store off `finding:created` - so writing there too would be a second, partial
   * copy of the same act. It was partial in practice: the batch it sent carried only
   * the labels that changed and left the rest alone, where the projection diffs the
   * finding's whole latest version.
   *
   * `roiChanges` is still on the payload for the callers that record them; nothing
   * here needs them now.
   */
  @invalidatesCache()
  public async updateRois(payload: UpdateRoisPayload): Promise<Roi[]> {
    const { unchangedRois, createdRois, updatedRois, deletedRois } = payload;
    const merged = [...unchangedRois, ...createdRois, ...updatedRois];

    try {
      const parseFindings = this.mapper.fromDomain(merged, deletedRois);
      const latestVersionsFindings = await this.parse.Object.saveAll(parseFindings, this.options as any);

      return latestVersionsFindings.flatMap((r) => TelespotRoiMapper.toDomain(r));
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  public async getById(id: string, signal?: AbortSignal): Promise<Roi | undefined> {
    const url = `${apiUrl('mosaics')}/rois/${encodeURIComponent(id)}`;
    const request$ = this.http.get<MosaicResponse>(url).pipe(
      retryWhen((errors) =>
        errors.pipe(
          concatMap((error, attempt) =>
            attempt < this.MAX_RETRY_ATTEMPTS - 1
              ? of(error).pipe(delay(this.RETRY_DELAY_MS * (attempt + 1)))
              : throwError(error)
          )
        )
      )
    );
    const result = await this.abortableHttp.toPromise(request$, signal);
    const items = MosaicMapper.mosaicsToRois(result.items).map((i) => ({ ...i, additionalLabelsIncluded: true }));
    return items.length > 0 ? items[0] : undefined;
  }

  /**
   * Fans out over {@link getById}, in chunks, tolerating individual failures: a caller
   * asking for a baseline is better served a partial map than nothing at all.
   */
  public async batchGetByIds(ids: string[]): Promise<Map<string, Roi>> {
    const unique = Array.from(new Set(ids));
    const rois = new Map<string, Roi>();

    for (let i = 0; i < unique.length; i += this.CONCURRENT_GETS) {
      const chunk = unique.slice(i, i + this.CONCURRENT_GETS);
      const settled = await Promise.allSettled(chunk.map((id) => this.getById(id)));

      for (const [index, outcome] of settled.entries()) {
        if (outcome.status === 'rejected') {
          console.warn(`[TelespotRoiRepository] batchGetByIds failed for ${chunk[index]}`, outcome.reason);
          continue;
        }

        if (outcome.value) rois.set(chunk[index], outcome.value);
      }
    }

    return rois;
  }

  public async fetchRoisByFindingIds(findingIds: string[]): Promise<Map<string, Roi>> {
    const ids = [...findingIds];
    const rois: Roi[] = [];

    try {
      while (ids.length > 0) {
        const batch = ids.splice(0, Math.min(this.BATCH_SIZE, ids.length));

        const latestVersionQuery = new this.parse.Query(LatestVersionTopology.TABLE)
          .equalTo(LatestVersionTopology.ENTITY, FindingTopology.TABLE)
          .containedIn(LatestVersionTopology.ENTITY_ID, batch);

        const findingsQuery = new this.parse.Query(FindingTopology.TABLE)
          .matchesKeyInQuery(ObjectTopology.ID, LatestVersionTopology.VERSION_ID, latestVersionQuery)
          .include(this.FINDING_FIELDS_TO_INCLUDE);

        const results = await findingsQuery.find();

        rois.push(...results.flatMap((r) => TelespotRoiMapper.toDomain(r)));
      }

      return new Map(rois.map((r) => [r.id, r]));
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  // `limit` and `nextToken` are left out of the key on purpose: this pages to the end of
  // the query, so they change how the answer is fetched but never what it holds. An empty
  // answer is not kept: an asset whose algorithm findings have yet to land reads as one.
  @cached({
    key: ({ sampleId, assetId, creatorEntity, creatorId }: ListForAssetInSamplePayload) =>
      `${sampleId}/${assetId}/${creatorEntity}/${creatorId}`,
    unless: ({ items }: ListRoisResponse) => items.length === 0,
  })
  public async listForAssetInSample(payload: ListForAssetInSamplePayload): Promise<ListRoisResponse> {
    const { assetId, sampleId, creatorEntity, creatorId } = payload;

    const analysisQuery = new this.parse.Query(AnalysisTopology.TABLE)
      .equalTo(AnalysisTopology.ASSET, this.subclasses.getSubclass(AssetTopology.TABLE).createWithoutData(assetId))
      .equalTo(AnalysisTopology.SAMPLE, this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(sampleId));

    if (creatorEntity === 'user')
      analysisQuery.equalTo(AnalysisTopology.CREATED_BY, this.parse.User.createWithoutData(creatorId));
    else if (creatorEntity === 'algorithm')
      analysisQuery.equalTo(
        AnalysisTopology.ALGORITHM,
        this.subclasses.getSubclass(AlgorithmTopology.TABLE).createWithoutData(creatorId)
      );

    const analysisFindingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesQuery(FindingTopology.ANALYSIS, analysisQuery)
      .containedIn(FindingTopology.TYPE, this.FINDING_TYPES)
      .equalTo(FindingTopology.CREATOR_ID, creatorId)
      .equalTo(FindingTopology.CREATOR_ENTITY, creatorEntity);

    const latestVersionQuery = new this.parse.Query(LatestVersionTopology.TABLE)
      .equalTo(LatestVersionTopology.ENTITY, FindingTopology.TABLE)
      .matchesKeyInQuery(LatestVersionTopology.VERSION_ID, ObjectTopology.ID, analysisFindingsQuery);

    const findingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesKeyInQuery(ObjectTopology.ID, LatestVersionTopology.VERSION_ID, latestVersionQuery)
      .include([FindingTopology.ANALYSIS, FindingTopology.PIPELINE_STEP]);

    const parseItems = this.getTokenPaginationGenerator({
      query: findingsQuery,
      limit: payload.limit ?? this.BATCH_SIZE,
    });

    const items: Roi[] = [];
    const roiFindings: RoiFinding[] = [];

    try {
      for await (const parseItem of parseItems) {
        for (const parseFinding of parseItem) {
          items.push(...TelespotRoiMapper.toDomain(parseFinding));

          const roiFinding = TelespotRoiMapper.toRoiFinding(parseFinding);
          if (roiFinding) {
            roiFindings.push(roiFinding);
          }
        }
      }

      return { items, roiFindings };
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  @cached({
    key: ({ sampleId, analystId }: ListForAnalystInSamplePayload) => `${sampleId}/${analystId}`,
    unless: (rois: Map<string, Roi>) => rois.size === 0,
  })
  public async fetchForAnalystInSample(payload: ListForAnalystInSamplePayload): Promise<Map<string, Roi>> {
    const sampleRef = this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(payload.sampleId);

    const analysisQuery = new this.parse.Query(AnalysisTopology.TABLE)
      .equalTo(AnalysisTopology.SAMPLE, sampleRef)
      .exists(AnalysisTopology.ASSET);

    const findingQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesQuery(FindingTopology.ANALYSIS, analysisQuery)
      .containedIn(FindingTopology.TYPE, this.FINDING_TYPES)
      .descending(ObjectTopology.CREATED_AT);

    const latestVersionsQuery = new this.parse.Query(LatestVersionTopology.TABLE)
      .equalTo(LatestVersionTopology.ENTITY, FindingTopology.TABLE)
      .matchesKeyInQuery(LatestVersionTopology.VERSION_ID, ObjectTopology.ID, findingQuery);

    const latestVersionFindingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesKeyInQuery(ObjectTopology.ID, LatestVersionTopology.VERSION_ID, latestVersionsQuery)
      .equalTo(FindingTopology.CREATOR_ID, payload.analystId)
      .include(this.FINDING_FIELDS_TO_INCLUDE);

    const parseItems = await this.getTokenPaginationGenerator({
      query: latestVersionFindingsQuery,
      limit: this.BATCH_SIZE,
    });

    const items: Roi[] = [];

    try {
      for await (const parseItem of parseItems) {
        items.push(...parseItem.flatMap((f) => TelespotRoiMapper.toDomain(f)));
      }

      return new Map(items.map((r) => [r.id, r]));
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  public async listWithLabelInSampleForAnalyst(payload: ListSampleRoisPayload) {
    const { sampleId, creatorId, label, nextToken, scoreMax, scoreMin, limit } = payload;

    let url = `${apiUrl('mosaics')}/samples/${sampleId}/rois?creator=${creatorId}&limit=${limit ?? this.BATCH_SIZE
      }&label=${label}`;

    if (scoreMax) url = url + `&scoreMax=${scoreMax}`;
    if (scoreMin) url = url + `&scoreMin=${scoreMin}`;

    url = !nextToken ? url : url + `&nextToken=${nextToken}`;

    const result = await this.http.get<MosaicResponse>(url).toPromise();

    return {
      items: MosaicMapper.mosaicsToRois(result.items),
      nextToken: result.nextToken,
    };
  }

  /**
   * How far the projection has got, as one request whatever the sample's size.
   *
   * Replaced a per-label count comparison: one request per non-empty label, each
   * paginating a whole partition of the ROIs index, and it could only agree with the
   * Parse-side counts while both applied the same display threshold. A version needs no
   * threshold at all.
   *
   * Asks for the whole partition rather than naming findings. The endpoint supports
   * both, and naming them is cheaper still, but the sample-wide answer is the one both
   * comparisons read - the locally-saved findings are in it too, since a partition read
   * cannot omit them.
   */
  public async getProjectionWatermarks(
    payload: ProjectionWatermarksPayload
  ): Promise<ProjectionWatermarksResponse> {
    const { sampleId, creatorId, findingIds } = payload;

    let url = `${apiUrl('mosaics')}/samples/${sampleId}/projections?creator=${creatorId}`;

    for (const findingId of findingIds ?? []) url += `&findings=${encodeURIComponent(findingId)}`;

    const result = await this.http.get<ProjectionResponse>(url).toPromise();

    const projectedVersions = new Map<string, FindingVersionRef>();

    for (const watermark of result?.watermarks ?? []) {
      projectedVersions.set(watermark.findingId, {
        version: watermark.projectedVersion,
        versionId: watermark.projectedVersionId,
      });
    }

    return { complete: Boolean(result?.complete), projectedVersions };
  }

  public getBlob(request: RoiCropRequest, signal?: AbortSignal): Promise<Blob> {
    return this.crops.getBlob(request, signal);
  }

  public async getSampleCounters(request: GetSampleCounterRequest) {
    const createdBy = {
      __type: 'Pointer',
      className: request.analystEntity === 'user' ? UserTopology.TABLE : AlgorithmTopology.TABLE,
      objectId: request.analystId,
    };

    try {
      const result = await Parse.Cloud.run('getSampleAnalsysStats', {
        sampleId: request.sampleId,
        createdBy,
      });

      const labelCounts = new Map<string, number>();
      const singleLabelCount = new Map<string, number>();
      const multiLabelCount = new Map<string, number>();

      for (const label of result.labelCount) labelCounts.set(label.labelId, label.rois);

      if (result.singleLabelCount) {
        for (const [labelId, count] of Object.entries(result.singleLabelCount)) {
          singleLabelCount.set(labelId, count as number);
        }
      }

      if (result.multiLabelCount) {
        for (const [labelCombination, count] of Object.entries(result.multiLabelCount)) {
          multiLabelCount.set(labelCombination, count as number);
        }
      }

      // Undefined, not an empty map, when the server cannot answer - either the field is
      // absent entirely or its entries predate `findingVersionId`. Both read as "nothing
      // known" rather than "nothing to expect": an empty map looks satisfied to a
      // shortfall comparison, and a missing id disagrees with every watermark.
      const reported = result.findingVersions as
        | { findingId: string; version: number; findingVersionId?: string }[]
        | undefined;

      const findingVersions = reported?.every(({ findingVersionId }) => !!findingVersionId)
        ? new Map<string, FindingVersionRef>(
          reported.map(({ findingId, version, findingVersionId }) => [
            findingId,
            { version, versionId: findingVersionId as string },
          ])
        )
        : undefined;

      return {
        totalCount: result.total,
        labelCounts,
        singleLabelCount,
        multiLabelCount,
        findingVersions,
      };
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }
}
